/* eslint-disable @typescript-eslint/naming-convention */

import React, { createContext, useContext } from 'react';
import type {
  CategoryPageSubheaderEntry,
  CustomComponentDefinition,
  CustomComponentParamField,
  CustomComponentsRegistry,
  CustomTagFilterConfig,
  Events,
  ListPageEntry,
  MetaFields,
  RemoteModuleRegistry,
  StoreFrontCarouselTitleProps,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { CategoryPageBelowListIterable } from 'src/dispensary/category/category-page-below-list';

// Re-exported under the same names so existing importers (e.g. sdk-custom-components.tsx,
// use-sdk-custom-component-config.ts, remote-theme.tsx, and their tests) don't need to change
// their import source now that the shim below is gone.
export type { CustomComponentDefinition, CustomComponentParamField, CustomComponentsRegistry };

type FallbackProps = {
  fallback?: React.FC<any>;
  pending?: React.FC<any>;
  renderRemote?: (component: React.ReactElement) => React.ReactElement;
};
type DefaultComponentSig = (options?: FallbackProps) => React.FC<any>;
type CategoryIterable = { category: ListPageEntry['category']; components: React.ElementType[] };

export type RouteablePage = {
  path: string;
  component: DefaultComponentSig;
};

// The `theme` prop forwarded verbatim to remote (module-federation) components — see
// remote-theme.tsx's `wrapComponentFactory` and README.md's "Accept and use the data and theme
// props". Today it's always the styled-components/MUI theme object built by `getStyledTheme`
// (theme/builders/styled.js, untyped JS) — never a string — but nothing in this subsystem reads
// any of its properties, so it's typed as an opaque plain object rather than that builder's
// specific (currently unnamed) shape.
export type RemoteTheme = Record<string, unknown> | null;

// TEMPORARY: Local shims for smart tags until @dutchiesdk/ecommerce-extensions-sdk exports them (ECOMM-12637)
export type CustomSmartTagFilterConfig = {
  category: string;
  name: string;
  title: string;
  match: string[];
};

// SDK 0.47.0 now publishes RemoteModuleRegistry.SpecialsCard itself (see RemoteModuleRegistry's
// own SpecialsCard field), so the ECOMM-13280 registry-field shim this used to carry is gone --
// only the narrower, still-unmet enableCustomSmartTagFilters shim (ECOMM-12637) remains here. The
// SpecialsCardProps DTOs in specials-card-types.ts are a separate, still-open part of ECOMM-13280.
// TODO: ECOMM-12637 - Remove enableCustomSmartTagFilters once @dutchiesdk/ecommerce-extensions-sdk exports it
export type ExtendedRemoteModuleRegistry = RemoteModuleRegistry & {
  enableCustomSmartTagFilters?: CustomSmartTagFilterConfig[];
};

export type RemoteModuleRegistryWithSmartTagFilters = ExtendedRemoteModuleRegistry;

export type ThemeConfig = {
  customTagFilters: CustomTagFilterConfig[];
  customSmartTagFilters: CustomSmartTagFilterConfig[];
};

export type CommerceComponents = {
  components: {
    [K in keyof Omit<
      ExtendedRemoteModuleRegistry,
      | 'CategoryPageBelowList'
      | 'CategoryPageInterstitials'
      | 'CategoryPageSlots'
      | 'CategoryPageSubheaders'
      | 'CustomComponents'
      | 'enableCustomSmartTagFilters'
      | 'enableCustomTagFilters'
      | 'events'
      | 'getStoreFrontMetaFields'
      | 'RouteablePages'
      | 'StoreFrontCarouselInterstitials'
      | 'StoreFrontCarouselTitles'
    >]-?: DefaultComponentSig;
  };
  findCarouselTitle: (o: {
    carouselId: string;
    fallback?: React.FC<StoreFrontCarouselTitleProps>;
  }) => React.FC<StoreFrontCarouselTitleProps>;
  findPage: (o: { fallback?: React.FC; path?: string }) => React.FC;
  /**
   * Resolves one SDK custom component by its registry id -- see `CustomComponentsRegistry`.
   * Mirrors `findPage`'s holding/fallback semantics (blank while the remote theme is still
   * loading, `fallback` once resolved with no matching/valid entry).
   */
  findCustomComponent: (o: { id: string; fallback?: React.FC<any> }) => React.FC<any>;
  /**
   * Raw, validated `CustomComponents` registry metadata (id, displayName, description, params)
   * -- NOT a render-ready component map. Used to build the CMS editor's dynamic SDK Components
   * palette entries (`createSdkCustomComponentBlocks`); use `findCustomComponent` to actually
   * render one.
   */
  customComponents: CustomComponentsRegistry;
  iterables: {
    StoreFrontCarouselInterstitials: () => React.ElementType[];
    CategoryPageInterstitials: () => CategoryIterable[];
    CategoryPageSlots: () => CategoryIterable[];
    CategoryPageBelowList: () => CategoryPageBelowListIterable[];
    /**
     * Not a component -- raw, unresolved SDK-authored entries for the
     * optional category page sub-header (ECOMM-13289). Consumers should
     * resolve/validate the matching entry via
     * `findMatchingCategorySubheaderText` rather than reading `text` off
     * these directly.
     */
    CategoryPageSubheaders: () => CategoryPageSubheaderEntry[];
  };
  pages: ({ fallback }: FallbackProps) => RouteablePage[];
  events: Events | undefined;
  isRemoteThemeResolved: boolean;
  themeConfig: ThemeConfig;
  /**
   * The current dispensary's remote theme module path (`customizationProvider.modulePath` in
   * `useRemoteCommerceComponents`), or `undefined` when no remote theme is configured for it.
   * Changes exactly when the active store's theme identity does -- including a plain store
   * switch to one with no theme at all -- so consumers that need to distinguish "the same
   * theme reloaded" from "a genuinely different theme/store" (e.g.
   * `useCmsConfigWithSdkComponents`'s cross-render accumulator) can key off it directly instead
   * of re-deriving dispensary/theme identity themselves.
   */
  modulePath: string | undefined;
  /**
   * The current store's full remote theme identity -- `manifestUrl`, `scope`, and `modulePath`
   * composed together -- or `undefined` when no remote theme is configured. `modulePath` alone
   * is NOT a safe proxy for "which theme": two different stores/manifests can legitimately
   * expose the same Module Federation `modulePath` (e.g. the conventional `'./Theme'` expose
   * name) while differing only in `manifestUrl`/`scope`, which is exactly what
   * `useLazyRemoteModule` itself keys its own reset on. Consumers that need to distinguish "the
   * same theme reloaded" from "a genuinely different theme/store" (e.g.
   * `useCmsConfigWithSdkComponents`'s cross-render accumulator) should key off this, not
   * `modulePath`.
   */
  themeIdentity: string | undefined;
  /**
   * Meta fields resolved from the remote theme's `getStoreFrontMetaFields` function, computed
   * once at the ThemeProvider level and shared by every consumer (Header, CanonicalTag,
   * DispensaryTags, ProductTags, JsonLDScript). Null when there is no remote theme, the theme
   * doesn't implement the function, the LaunchDarkly rollout flag
   * (`growth.ecomm.get-store-front-meta-fields-function.rollout`) is off, or extraction hasn't
   * resolved yet. Centralizing this avoids calling the merchant theme's function once per
   * consumer per navigation.
   */
  metaFields: MetaFields | null;
};

const EmptyComponent: React.FC<any> = () => <></>;

export const defaultCommerceComponents: CommerceComponents = {
  components: {
    StoreFrontHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontNavigation: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontFooter: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    GlobalStyles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoyaltyWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoginWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontSpecialsCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ListPageHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsPrimary: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCardListItem: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    FilterSidePanel: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsPageHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
  },
  findCarouselTitle: ({ fallback = EmptyComponent }) => fallback,
  findPage: ({ fallback = () => <></> }) => fallback,
  findCustomComponent: ({ fallback = EmptyComponent }) => fallback,
  customComponents: {},
  iterables: {
    StoreFrontCarouselInterstitials: () => [],
    CategoryPageInterstitials: () => [],
    CategoryPageSlots: () => [],
    CategoryPageBelowList: () => [],
    CategoryPageSubheaders: () => [],
  },

  pages: (_props: FallbackProps = { fallback: () => <></> }) => [],
  events: {},
  isRemoteThemeResolved: false,
  themeConfig: { customTagFilters: [], customSmartTagFilters: [] },
  modulePath: undefined,
  themeIdentity: undefined,
  metaFields: null,
};

export const CommerceComponentsContext = createContext<CommerceComponents>(defaultCommerceComponents);

export const useCommerceComponents = (): CommerceComponents => useContext(CommerceComponentsContext);
